import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useEffect, useState, useRef } from 'react';

interface StatItem {
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

const stats: StatItem[] = [
  { value: 58, suffix: '', label: 'California Counties Covered', icon: 'ri-map-2-line' },
  { value: 48, suffix: 'hr', label: 'Average Deployment Time', icon: 'ri-time-line' },
  { value: 99, suffix: '%', label: 'Equipment Uptime Guarantee', icon: 'ri-shield-check-line' },
];

function AnimatedCounter({ target, suffix, isVisible }: { target: number; suffix: string; isVisible: boolean }) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;
    const duration = 1800;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, target]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
}

export default function CoverageStats() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  return (
    <section ref={ref} className="py-16 sm:py-20 px-4 sm:px-6 bg-[#F7F9FB]">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`text-center transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-4 bg-white rounded-xl shadow-sm flex items-center justify-center">
                <i className={`${stat.icon} text-slate-blue text-xl sm:text-2xl`}></i>
              </div>
              <div className="text-navy text-[28px] sm:text-[36px] lg:text-[42px] font-bold leading-none mb-2">
                <AnimatedCounter target={stat.value} suffix={stat.suffix} isVisible={isVisible} />
              </div>
              <p className="text-[#5A6C7D] text-xs sm:text-sm font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
